package com.unrealmi.site.components.sections

import androidx.compose.runtime.*
import com.varabyte.kobweb.browser.dom.ElementTarget
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.icons.CloseIcon
import com.varabyte.kobweb.silk.components.icons.HamburgerIcon
import com.varabyte.kobweb.silk.components.icons.MoonIcon
import com.varabyte.kobweb.silk.components.icons.SunIcon
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.overlay.Overlay
import com.varabyte.kobweb.silk.components.overlay.OverlayVars
import com.varabyte.kobweb.silk.components.overlay.PopupPlacement
import com.varabyte.kobweb.silk.components.overlay.Tooltip
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.style.breakpoint.displayUntil
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import com.unrealmi.site.brandGradient
import com.unrealmi.site.components.widgets.IconButton
import com.unrealmi.site.gradientText
import com.unrealmi.site.toSitePalette

val NavHeaderStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .position(Position.Sticky)
            .top(0.px)
            .zIndex(100)
            .padding(topBottom = 0.85.cssRem, leftRight = clamp(1.cssRem, 5.percent, 3.cssRem))
            .styleModifier {
                property("backdrop-filter", "blur(16px)")
                property("-webkit-backdrop-filter", "blur(16px)")
            }
            .borderBottom(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
    }
}

@Composable
private fun BrandMark() {
    Link("/", Modifier.textDecorationLine(TextDecorationLine.None)) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            // Gradient "logo" glyph.
            Box(
                Modifier
                    .size(2.1.cssRem)
                    .borderRadius(0.6.cssRem)
                    .styleModifier {
                        property("background", brandGradient(135))
                        property("box-shadow", "0 6px 18px rgba(124,92,255,0.45)")
                    },
            )
            Column(Modifier.margin(left = 0.7.cssRem)) {
                SpanText(
                    "UnrealMi",
                    Modifier.fontSize(1.15.cssRem).fontWeight(700).lineHeight(1.1).gradientText(),
                )
                SpanText(
                    "虚米网络",
                    Modifier
                        .fontSize(0.65.cssRem)
                        .letterSpacing(3.px)
                        .color(ColorMode.current.toSitePalette().subtle),
                )
            }
        }
    }
}

// A lightweight Box helper (avoids importing foundation Box just for the brand glyph).
@Composable
private fun Box(modifier: Modifier) {
    org.jetbrains.compose.web.dom.Div(attrs = modifier.toAttrs())
}

val NavLinkStyle = CssStyle {
    base {
        Modifier
            .color(colorMode.toSitePalette().subtle)
            .fontSize(0.95.cssRem)
            .textDecorationLine(TextDecorationLine.None)
            .transition(Transition.of("color", 150.ms))
    }
    hover { Modifier.color(colorMode.toSitePalette().brand.primary) }
}

@Composable
private fun NavLink(path: String, text: String) {
    Link(path, text, NavLinkStyle.toModifier(), variant = UndecoratedLinkVariant.then(UncoloredLinkVariant))
}

@Composable
private fun MenuItems() {
    NavLink("/#capabilities", "核心能力")
    NavLink("/#vision", "愿景使命")
    NavLink("/about", "关于我们")
}

@Composable
private fun CtaButton() {
    Link(
        "mailto:contact@unrealmi.com",
        Modifier
            .textDecorationLine(TextDecorationLine.None)
            .padding(topBottom = 0.5.cssRem, leftRight = 1.1.cssRem)
            .borderRadius(2.cssRem)
            .fontSize(0.9.cssRem)
            .fontWeight(600)
            .color(Colors.White)
            .cursor(Cursor.Pointer)
            .styleModifier {
                property("background", brandGradient(120))
                property("box-shadow", "0 8px 20px rgba(124,92,255,0.35)")
            },
    ) {
        SpanText("联系我们")
    }
}

@Composable
private fun ColorModeButton() {
    var colorMode by ColorMode.currentState
    IconButton(onClick = { colorMode = colorMode.opposite }) {
        if (colorMode.isLight) MoonIcon() else SunIcon()
    }
    Tooltip(ElementTarget.PreviousSibling, "切换明暗模式", placement = PopupPlacement.BottomRight)
}

@Composable
private fun HamburgerButton(onClick: () -> Unit) {
    IconButton(onClick) { HamburgerIcon() }
}

@Composable
private fun CloseButton(onClick: () -> Unit) {
    IconButton(onClick) { CloseIcon() }
}

val SideMenuSlideInAnim = Keyframes {
    from { Modifier.translateX(100.percent) }
    to { Modifier }
}

enum class SideMenuState {
    CLOSED,
    OPEN,
    CLOSING;

    fun close() = when (this) {
        CLOSED -> CLOSED
        OPEN -> CLOSING
        CLOSING -> CLOSING
    }
}

@Composable
fun NavHeader() {
    Row(NavHeaderStyle.toModifier(), verticalAlignment = Alignment.CenterVertically) {
        BrandMark()

        Spacer()

        Row(
            Modifier.gap(2.cssRem).displayIfAtLeast(Breakpoint.MD),
            verticalAlignment = Alignment.CenterVertically
        ) {
            MenuItems()
            ColorModeButton()
            CtaButton()
        }

        Row(
            Modifier
                .fontSize(1.5.cssRem)
                .gap(1.cssRem)
                .displayUntil(Breakpoint.MD),
            verticalAlignment = Alignment.CenterVertically
        ) {
            var menuState by remember { mutableStateOf(SideMenuState.CLOSED) }

            ColorModeButton()
            HamburgerButton(onClick = { menuState = SideMenuState.OPEN })

            if (menuState != SideMenuState.CLOSED) {
                SideMenu(
                    menuState,
                    close = { menuState = menuState.close() },
                    onAnimationEnd = { if (menuState == SideMenuState.CLOSING) menuState = SideMenuState.CLOSED }
                )
            }
        }
    }
}

@Composable
private fun SideMenu(menuState: SideMenuState, close: () -> Unit, onAnimationEnd: () -> Unit) {
    Overlay(
        Modifier
            .setVariable(OverlayVars.BackgroundColor, Colors.Black.copyf(alpha = 0.5f))
            .onClick { close() }
    ) {
        key(menuState) { // Force recompute animation parameters when close button is clicked
            Column(
                Modifier
                    .fillMaxHeight()
                    .width(clamp(12.cssRem, 40.percent, 16.cssRem))
                    .align(Alignment.CenterEnd)
                    .padding(top = 1.cssRem, leftRight = 1.cssRem)
                    .gap(1.5.cssRem)
                    .backgroundColor(ColorMode.current.toSitePalette().nearBackground)
                    .borderLeft(1.px, LineStyle.Solid, ColorMode.current.toSitePalette().border)
                    .animation(
                        SideMenuSlideInAnim.toAnimation(
                            duration = 200.ms,
                            timingFunction = if (menuState == SideMenuState.OPEN) AnimationTimingFunction.EaseOut else AnimationTimingFunction.EaseIn,
                            direction = if (menuState == SideMenuState.OPEN) AnimationDirection.Normal else AnimationDirection.Reverse,
                            fillMode = AnimationFillMode.Forwards
                        )
                    )
                    .borderRadius(topLeft = 1.5.cssRem, bottomLeft = 1.5.cssRem)
                    .onClick { it.stopPropagation() }
                    .onAnimationEnd { onAnimationEnd() },
                horizontalAlignment = Alignment.End
            ) {
                CloseButton(onClick = { close() })
                Column(
                    Modifier.padding(right = 0.75.cssRem).gap(1.5.cssRem).fontSize(1.4.cssRem),
                    horizontalAlignment = Alignment.End
                ) {
                    MenuItems()
                }
            }
        }
    }
}
